import React, { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Donor } from "@shared/interfaces/ProgramFiles";
import { useDonor } from "@pages/Program/utils/useDonor";
import Loader from "@shared/components/Loader";

interface MultiSelectDonorDropdownProps {
  selected: Donor[];
  onChange: (selected: Donor[]) => void;
  fetchOptions: (query: string) => Promise<Donor[]>;
  placeholder?: string;
  setAllDonors: React.Dispatch<React.SetStateAction<Donor[]>>;
  loading?: boolean;
  disabled?: boolean;
}

const MultiSelectDonorDropdown: React.FC<MultiSelectDonorDropdownProps> = ({
  selected,
  onChange,
  fetchOptions,
  placeholder = "Search or create donor...",
  setAllDonors,
  loading,
  disabled,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<Donor[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const { saveDonor } = useDonor();

  useEffect(() => {
    if (query.length < 1) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchOptions(query);
        const filtered = results.filter(
          (opt) => !selected.some((s) => s.id === opt.id)
        );
        setOptions(filtered);
        setIsOpen(true);
      } catch (err) {
        console.error("Donor search failed:", err);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const closeOnClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, []);

  const handleSelect = (option: Donor) => {
    onChange([...selected, option]);
    setQuery("");
    setOptions([]);
    setIsOpen(false);
  };

  const handleRemove = (option: Donor) => {
    onChange(selected.filter((s) => s.id !== option.id));
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }

    if (e.key === "Enter" && query.trim()) {
      const alreadyExists = options.some(
        (opt) => opt.name.toLowerCase() === query.trim().toLowerCase()
      );
      if (!alreadyExists) {
        try {
          const newDonor = await saveDonor({ name: query.trim() });

          if (newDonor && newDonor.id) {
            const updated = [...selected, newDonor];
            onChange(updated);
            setAllDonors((prev) => [...prev, newDonor]);
          }

          setQuery("");
          setOptions([]);
          setIsOpen(false);
        } catch (err) {
          console.error("Error saving donor:", err);
        }
      }
    }
  };

  return (
    <div
      className={`w-full relative ${disabled && "bg-[#FAFAFA]"}`}
      ref={wrapperRef}
    >
      <div
        className="flex flex-wrap gap-1 p-2 border border-gray-300 rounded-md h-10 2xl:h-12 cursor-text relative"
        onClick={() => setIsOpen(true)}
      >
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 ">
            <div className="relative">
              <div className="absolute right-3 top-1/2 transform translate-y-1/2">
                <Loader color="#777" />
              </div>
            </div>
          </div>
        )}
        {disabled && (
          <div className="absolute inset-0">
            <div className="relative"></div>
          </div>
        )}
        {selected.map((option) => (
          <span
            key={option.id}
            className="flex items-center bg-blue-100 text-blue-700 text-xs 2xl:text-sm px-2 py-1 rounded-full"
          >
            {option.name}
            {!disabled && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(option);
                }}
                className="ml-1 text-blue-500 hover:text-blue-700"
              >
                &times;
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 focus:outline-none min-w-[10ch] bg-transparent text-sm 2xl:text-base"
          placeholder={selected.length === 0 ? placeholder : ""}
        />
      </div>

      {isOpen && query.length >= 1 && (
        <ul className="absolute z-10 bg-white border text-sm 2xl:text-base rounded-lg mt-1 w-full max-h-48 overflow-auto shadow-md">
          {options.map((option) => (
            <li
              key={option.id}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
              onClick={() => handleSelect(option)}
            >
              {option.name}
            </li>
          ))}
          {query &&
            !options.some(
              (opt) => opt.name.toLowerCase() === query.trim().toLowerCase()
            ) && (
              <li className="px-3 py-2 text-gray-500 text-xs 2xl:text-sm italic">
                Press <strong>Enter</strong> to create donor: "{query.trim()}"
              </li>
            )}
        </ul>
      )}
    </div>
  );
};

export default MultiSelectDonorDropdown;
